"use client";

import React from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TeamsCombobox, TeamsProvider } from "@/components/ui/TeamsCombobox";
import { HelpSidebar } from "@/app/components/onboarding/HelpSidebar";
import { X, Plus, Check } from "lucide-react";

export default function ModalTeamSelectionExample() {
  const [open, setOpen] = React.useState(true);
  const [selected, setSelected] = React.useState<string[]>([]);
  const [additionalTeams, setAdditionalTeams] = React.useState<{ id: string; name: string }[]>([]);
  const [newTeam, setNewTeam] = React.useState("");
  const [showHelp, setShowHelp] = React.useState(true);
  const [isSaving, setIsSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);

  const removeSelected = (name: string) => setSelected(prev => prev.filter(v => v !== name));
  const removeAdditional = (id: string) => setAdditionalTeams(prev => prev.filter(v => v.id !== id));

  const handleAddTeam = () => {
    if (!newTeam.trim()) return;
    setAdditionalTeams(prev => [...prev, { id: Date.now().toString(), name: newTeam.trim() }]);
    setNewTeam("");
  };

  const handleSave = async () => {
    setIsSaving(true);
    await new Promise(r => setTimeout(r, 1000));
    setIsSaving(false);
    setSaved(true);
  };

  return (
    <div className="space-y-4">
      <Button onClick={() => setOpen(true)}>Open Team Selection Modal</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-6xl! w-6xl! bg-transparent shadow-none border-0 p-0">
          <div className="bg-card/95 backdrop-blur-xl rounded-xl w-full overflow-hidden shadow-2xl border border-white/10 flex flex-col" style={{ minHeight: "70vh" }}>
            <div className="p-6 pb-4 border-b border-white/10 bg-gradient-to-b from-primary/5 to-transparent">
              <h2 className="text-3xl font-bold text-foreground">Select Your Teams</h2>
              <p className="mt-2 text-muted-foreground">Choose existing teams or add new ones by name.</p>
            </div>

            <div className="p-6 space-y-6 flex-1 overflow-y-auto">
              <div className="space-y-2">
                <h3 className="text-xl font-semibold text-foreground">Teams</h3>
                <div className="space-y-3">
                  {selected.map(name => (
                    <div key={name} className="flex items-center justify-between p-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl hover:bg-white/15 transition-all duration-200 shadow-sm">
                      <div className="flex items-center">
                        <div className="h-10 w-10 rounded-full bg-primary/20 backdrop-blur-sm flex items-center justify-center text-primary border border-primary/30">
                          <span className="text-sm font-semibold">{name.slice(0,2).toUpperCase()}</span>
                        </div>
                        <div className="ml-3">
                          <p className="text-sm font-medium text-foreground">{name}</p>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/20 text-primary border border-primary/30 mt-1">Existing Team</span>
                        </div>
                      </div>
                      <button type="button" onClick={() => removeSelected(name)} className="text-muted-foreground hover:text-destructive transition-colors rounded-lg p-2 hover:bg-white/5" aria-label="Remove">
                        <X className="h-5 w-5" />
                      </button>
                    </div>
                  ))}
                  {additionalTeams.map(t => (
                    <div key={t.id} className="flex items-center justify-between p-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl hover:bg-white/15 transition-all duration-200 shadow-sm">
                      <div className="flex items-center">
                        <div className="h-10 w-10 rounded-full bg-primary/20 backdrop-blur-sm flex items-center justify-center text-primary border border-primary/30">
                          <span className="text-sm font-semibold">{t.name.slice(0,2).toUpperCase()}</span>
                        </div>
                        <div className="ml-3">
                          <p className="text-sm font-medium text-foreground">{t.name}</p>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/20 text-primary border border-primary/30 mt-1">Added Manually</span>
                        </div>
                      </div>
                      <button type="button" onClick={() => removeAdditional(t.id)} className="text-muted-foreground hover:text-destructive transition-colors rounded-lg p-2 hover:bg-white/5" aria-label="Remove">
                        <X className="h-5 w-5" />
                      </button>
                    </div>
                  ))}
                  {selected.length + additionalTeams.length === 0 && (
                    <div className="text-sm text-muted-foreground">No teams selected yet.</div>
                  )}
                </div>
              </div>

              <div className="space-y-3">
                <h4 className="text-sm font-medium text-foreground">Add Teams</h4>
                <TeamsProvider>
                  <TeamsCombobox selected={selected} setSelected={setSelected} fullWidth />
                </TeamsProvider>
                <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 bg-white/5 backdrop-blur-sm border border-white/20 rounded-xl p-4">
                  <div>
                    <label htmlFor="teamName" className="block text-xs font-medium text-foreground mb-1">Team name</label>
                    <Input id="teamName" value={newTeam} onChange={(e) => setNewTeam(e.target.value)} className="bg-white/10 border-white/20" />
                  </div>
                  <div className="flex items-end">
                    <Button type="button" variant="outline" onClick={handleAddTeam} disabled={!newTeam.trim()} className="backdrop-blur-sm border-white/20 hover:bg-white/10">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Team
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t border-white/10 bg-gradient-to-t from-primary/5 to-transparent backdrop-blur-sm px-6 py-4 flex justify-between items-center">
              <Button type="button" variant="outline" onClick={() => setShowHelp(true)} className="backdrop-blur-sm border-white/20 hover:bg-white/10">Help</Button>
              <div className="flex space-x-3">
                <Button type="button" variant="outline" onClick={() => setOpen(false)} className="backdrop-blur-sm border-white/20 hover:bg-white/10">Close</Button>
                <Button type="button" onClick={handleSave} disabled={isSaving || saved || (selected.length + additionalTeams.length) === 0} className="flex items-center bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20">
                  {isSaving ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                      Saving...
                    </>
                  ) : saved ? (
                    <>
                      <Check className="h-4 w-4 mr-2" />
                      Saved
                    </>
                  ) : (
                    <>Save Teams</>
                  )}
                </Button>
                <Button type="button" onClick={() => setOpen(false)} className="min-w-[120px] bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20">Continue</Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <HelpSidebar
        isOpen={showHelp}
        onClose={() => setShowHelp(false)}
        faqs={[
          { question: 'How should I structure my teams?', answer: 'Sint laborum labore excepteur laboris. Quis occaecat mollit dolor occaecat est nisi labore. Labore laborum dolore aute eu ex. Mollit do eu nulla sint velit sint deserunt elit exercitation tempor. Velit aute ea voluptate qui eu aute aute quis fugiat aliquip. Occaecat commodo elit elit culpa. Aliquip sunt proident fugiat sint esse sit eu officia. Laboris Lorem Lorem dolore duis cupidatat proident minim.' },
          { question: 'Can I add teams later?', answer: "Fugiat consectetur do pariatur amet mollit laboris dolore. Sint quis nostrud ea aute enim excepteur eu. Tempor pariatur deserunt voluptate dolore nostrud voluptate mollit eu. Labore dolore ut nulla in ad id ullamco sunt amet tempor nostrud nostrud. Anim nulla aliquip do esse non.\n\nEsse amet cupidatat eu ea dolor ut et consequat. Aute enim dolor deserunt dolore. Velit velit officia commodo mollit dolor sit aliqua nulla ad magna ad. Ex amet dolore nisi nulla. Cupidatat nisi aliquip id quis excepteur consectetur esse culpa Lorem. Do velit reprehenderit nostrud. Ad minim sunt nulla sunt ut proident est esse ad duis nisi veniam amet tempor ea.\n\nSint elit ullamco non aliqua proident elit reprehenderit. Elit sunt aute commodo nulla adipisicing enim reprehenderit. Adipisicing mollit quis sint incididunt sint occaecat. Deserunt non aute et veniam voluptate ullamco officia tempor irure qui mollit ipsum cupidatat anim. Eiusmod officia commodo qui ipsum ad sit sit reprehenderit elit consequat qui mollit proident sunt. Nisi amet eu excepteur laborum quis. Nisi ut culpa et in amet eiusmod qui eu non. Anim deserunt ipsum nostrud duis fugiat laborum id dolor tempor elit fugiat."
           },
        ]}
        videoTitle="Managing Compliance Teams"
        videoUrl="https://www.youtube.com/embed/example-team-video"
      />
    </div>
  );
}
